import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

const FLAVOR_ATTRS = ['sweet','salty','sour','bitter','umami','heat','aroma_intensity'];
const NUTRI_FIELDS = [
  ['energy_kcal','Energy (kcal)'],['energy_kj','Energy (kJ)'],['protein','Protein (g)'],['fat','Fat (g)'],
  ['sat_fat','Sat Fat (g)'],['trans_fat','Trans Fat (g)'],['carbs','Carbs (g)'],['sugars','Sugars (g)'],
  ['added_sugars','Added Sugars (g)'],['fiber','Fiber (g)'],['sodium_mg','Sodium (mg)'],['salt_g','Salt (g)']
];

export default function ItemDetailDrawer({ item, onClose }) {
  const [flavor, setFlavor] = useState(null);
  const [nutri, setNutri] = useState(null);
  const [savingF, setSavingF] = useState(false);
  const [savingN, setSavingN] = useState(false);

  useEffect(() => {
    if (!item) return;
    setFlavor(null); setNutri(null);
    API.get('/flavor/seasoning/' + encodeURIComponent(item.item_code))
      .then(r => setFlavor(r.data.manual || {})).catch(() => setFlavor({}));
    API.get('/nutrition/item/' + encodeURIComponent(item.item_code))
      .then(r => setNutri(r.data || {})).catch(() => setNutri({}));
  }, [item]);

  if (!item) return null;

  const saveFlavor = async () => {
    setSavingF(true);
    try {
      await API.put('/flavor/seasoning/' + encodeURIComponent(item.item_code) + '/manual',
        { ...FLAVOR_ATTRS.reduce((o, a) => ({ ...o, [a]: parseFloat(flavor[a]) || 0 }), {}), notes: flavor.notes || 'human-edited' });
      toast.success('Flavor saved');
    } catch (e) { toast.error('Save failed'); }
    setSavingF(false);
  };

  const saveNutri = async () => {
    setSavingN(true);
    try {
      await API.put('/nutrition/item/' + encodeURIComponent(item.item_code),
        NUTRI_FIELDS.reduce((o, [k]) => ({ ...o, [k]: parseFloat(nutri[k]) || 0 }), {}));
      toast.success('Nutrition saved');
    } catch (e) { toast.error('Save failed'); }
    setSavingN(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,8,18,0.45)', zIndex: 900 }}>
      <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 460, maxWidth: '96%',
        background: 'var(--bg-card, #fff)', boxShadow: '-8px 0 32px rgba(0,0,0,0.25)', overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{item.item_name}</div>
            <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent)' }}>{item.item_code} · {item.classification}{item.rm_subtype ? ' / ' + item.rm_subtype : ''}</div>
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>✕</button>
        </div>

        {/* Flavor */}
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center' }}>
            👅 Flavor Profile
            {flavor && flavor.is_estimated === 1 && <span style={{ marginLeft: 8, fontSize: 9, background: '#fff3e0', color: '#e65100', borderRadius: 8, padding: '2px 8px' }}>AI-estimated</span>}
            {flavor && flavor.is_estimated === 0 && <span style={{ marginLeft: 8, fontSize: 9, background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, padding: '2px 8px' }}>human</span>}
          </div>
          {!flavor ? 'Loading…' : (
            <>
              {FLAVOR_ATTRS.map(a => (
                <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0' }}>
                  <div style={{ width: 110, fontSize: 12, textTransform: 'capitalize' }}>{a.replace('_', ' ')}</div>
                  <input type="range" min="0" max="10" step="0.1" value={flavor[a] || 0}
                    onChange={e => setFlavor(f => ({ ...f, [a]: e.target.value }))} style={{ flex: 1 }} />
                  <div style={{ width: 34, fontSize: 12, textAlign: 'right', fontFamily: 'monospace' }}>{Number(flavor[a] || 0).toFixed(1)}</div>
                </div>
              ))}
              {flavor.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{flavor.notes}</div>}
              <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={savingF} onClick={saveFlavor}>💾 Save Flavor</button>
            </>
          )}
        </div>

        {/* Nutrition */}
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center' }}>
            🥗 Nutrition (per 100g)
            {nutri && nutri.is_estimated === 1 && <span style={{ marginLeft: 8, fontSize: 9, background: '#fff3e0', color: '#e65100', borderRadius: 8, padding: '2px 8px' }}>AI-estimated</span>}
            {nutri && nutri.is_estimated === 0 && <span style={{ marginLeft: 8, fontSize: 9, background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, padding: '2px 8px' }}>human</span>}
          </div>
          {!nutri ? 'Loading…' : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px' }}>
                {NUTRI_FIELDS.map(([k, label]) => (
                  <label key={k} style={{ fontSize: 11 }}>
                    {label}
                    <input type="number" className="form-input" style={{ width: '100%', padding: '4px 8px', fontSize: 12 }}
                      value={nutri[k] ?? ''} onChange={e => setNutri(n => ({ ...n, [k]: e.target.value }))} />
                  </label>
                ))}
              </div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={savingN} onClick={saveNutri}>💾 Save Nutrition</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
